import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { SpecProjectNotes } from '../../../lib/api/spec-requirements.js';

export interface ProjectNotesEditorProps {
  /** 今保存されている補足欄。無い案件では null */
  projectNotes: SpecProjectNotes | null;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因。文字数上限・空欄はここに API のメッセージが入る */
  error: unknown;
  /** 保存が通ったときに残った注意（上限に近づいた） */
  warnings: string[];
  /** 保存する */
  onSave: (body: string) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/**
 * 補足欄を編集する。表ではなく 1 枚のテキストエリア（markdown）。
 *
 * **判定は API が持つ。** 空欄・文字数上限の判定は `check_project_notes` と同じ規則で API 側にある。
 * 画面は**今の文字数だけ**を出し、超過は保存時に返るメッセージをそのまま見せる。上限の数を画面に
 * 持たせると、変わったときに黙ってずれる。
 *
 * **行数は縛らない。** 自由欄には「表の行」という概念が無く、同じ内容でも見出し・箇条書き・表の
 * どれで書くかで行数は何倍にも変わる。だから縛るのは文字数だけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の補足欄（markdown の自由欄）をテキストエリアで編集して保存する
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function ProjectNotesEditor({
  projectNotes,
  saving,
  error,
  warnings,
  onSave,
  onCancel,
}: ProjectNotesEditorProps) {
  const { t } = useTranslation();
  const [body, setBody] = useState(projectNotes?.body ?? '');

  /** 保存できなかった理由を人が読む形にする。 */
  const reason = (cause: unknown): string => {
    if (cause instanceof ApiError) {
      if (cause.status === 403) return t('spec.projectNotes.forbidden');
      return cause.message;
    }
    if (cause instanceof Error) return cause.message;
    return t('spec.projectNotes.saveFailed');
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(body);
      }}
    >
      <p className="text-xs text-[var(--color-text-muted)]">{t('spec.projectNotes.hint')}</p>
      <p className="text-xs text-[var(--color-text-muted)]">{t('spec.projectNotes.notReviewed')}</p>

      <textarea
        className="mt-2 block w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm font-mono"
        rows={16}
        value={body}
        aria-label={t('spec.projectNotes.bodyLabel')}
        onChange={(e) => {
          setBody(e.target.value);
        }}
      />
      <span className="text-xs text-[var(--color-text-muted)]">
        {t('spec.projectNotes.chars', { chars: body.length })}
      </span>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(error)}
        </p>
      )}

      {warnings.length === 0 ? null : (
        <div className="mt-3 text-sm text-[var(--color-text-primary)]">
          <p className="font-semibold text-[var(--color-warning-text)]">
            {t('spec.projectNotes.warnings')}
          </p>
          <ul className="list-disc pl-5">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {saving ? t('spec.projectNotes.saving') : t('spec.projectNotes.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.projectNotes.cancel')}
        </button>
      </div>
    </form>
  );
}
